// Состояние партии и базовые хелперы. Всё состояние — простой JSON-объект G,
// чтобы его можно было сохранять в localStorage и передавать по сети.
import type {
  BossId, BotLevel, BotStyle, CardDef, CardId, CardInst, CardRef, CardsData, CardTag, CardType, DdkDef, DdkId, EventBody, Game, GainWhere, GlobalZone,
  MutatorId, Player, PlayerId, PlayerZone, Rng, SetupState, TraitDef, TraitId, Turn,
} from '../types.ts';
import { makeRng, shuffle } from './rng.ts';
import { handSizeOf, normalizeMutators, startLifeOf } from './mutators.ts';
import { BOSSES, bossName, isBossId } from './bosses.ts';
import { RULES, RULES_VERSION } from './rules.ts';
import { REBALANCE, REBALANCE_TRAIT_NOTES, runModeHooks, priceTable, pricePatchedIds } from './rebalance.ts';

export interface Database {
  cards: Record<CardId, CardDef>;
  traits: Record<TraitId, TraitDef>;
  ddk: Record<DdkId, DdkDef>;
  byType: Partial<Record<CardType, CardDef[]>>;
  raw: CardsData | null;
}

export const DB: Database = { cards: {}, traits: {}, ddk: {}, byType: {}, raw: null };

let nextIid = 1;
function mk(cid: CardId): CardInst {
  return { iid: nextIid++, cid };
}

export function loadDatabase(data: CardsData): void {
  if (!data || !Array.isArray(data.cards) || !Array.isArray(data.traits) || !Array.isArray(data.ddk)) {
    throw new Error('Некорректная база карт: ожидаются массивы cards, traits, ddk');
  }
  DB.cards = {};
  DB.byType = {};
  for (const c of data.cards) {
    if (!Array.isArray(c.tags)) throw new Error(`Карта ${c.id}: нет поля tags`);
    DB.cards[c.id] = c;
    (DB.byType[c.type] ??= []).push(c);
  }
  DB.traits = {};
  for (const t of data.traits) DB.traits[t.id] = t;
  DB.ddk = {};
  for (const t of data.ddk) DB.ddk[t.id] = t;
  DB.raw = data;
}

/** Описание карты по экземпляру или cid. Бросает ошибку, если карты нет в базе. */
export function card(c: CardRef | CardId): CardDef {
  const cid = typeof c === 'string' ? c : c.cid;
  const d = DB.cards[cid];
  if (!d) throw new Error(`Неизвестная карта: ${cid}`);
  return d;
}

/** Описание карты или null (для мест, где карта может быть не из базы) */
export function cardOrNull(c: CardRef | CardId | null | undefined): CardDef | null {
  if (c == null) return null;
  const cid = typeof c === 'string' ? c : c.cid;
  return DB.cards[cid] ?? null;
}

export function hasTag(c: CardRef | CardId, tag: CardTag): boolean {
  return card(c).tags.includes(tag);
}

export function traitOf(p: Player): TraitDef | null {
  return p.traitId ? DB.traits[p.traitId] ?? null : null;
}

export function traitDef(id: TraitId): TraitDef {
  const t = DB.traits[id];
  if (!t) throw new Error(`Неизвестный жетон свойства: ${id}`);
  return t;
}

export function ddkDef(id: DdkId): DdkDef {
  const t = DB.ddk[id];
  if (!t) throw new Error(`Неизвестный жетон дохлого колдуна: ${id}`);
  return t;
}

/** Штраф за жетон дохлого колдуна в конце игры: базовые −3 плюс звезда на жетоне */
export function ddkPenalty(t: DdkDef): number {
  return RULES.ddkBasePenalty + (t.vp ?? 0);
}

/** Игрок по id. Бросает ошибку на несуществующий id — это всегда баг. */
export function player(G: Game, id: PlayerId): Player {
  const p = G.players[id];
  if (!p) throw new Error(`Нет игрока с id ${id}`);
  return p;
}

export function turnOf(G: Game): Turn {
  if (!G.turn) throw new Error('Ход ещё не начался');
  return G.turn;
}

export function rngOf(G: Game): Rng {
  if (!G.rng) throw new Error('Нет генератора случайных чисел: это представление партии, а не полное состояние');
  return G.rng;
}

export function setupOf(G: Game): SetupState {
  if (!G.setup) throw new Error('Фаза подготовки уже завершена');
  return G.setup;
}

/** Игрок закончил первую фазу подготовки: есть и жетон, и фамильяр (с v11 жетон выбирается раньше фамильяра) */
export function setupDone(p: Pick<Player, 'traitId' | 'familiars'>): boolean {
  return !!p.traitId && p.familiars.length > 0;
}

/** Глубокая копия JSON-подобного значения (быстрее JSON.parse(JSON.stringify)); undefined-поля опускаются, как в JSON */
export function deepClone<T>(v: T): T {
  if (v === null || typeof v !== 'object') return v;
  if (Array.isArray(v)) {
    const a = new Array<unknown>(v.length);
    for (let i = 0; i < v.length; i++) a[i] = deepClone(v[i]);
    return a as T;
  }
  const o: Record<string, unknown> = {};
  for (const k in v) {
    const x = (v as Record<string, unknown>)[k];
    if (x !== undefined) o[k] = deepClone(x);
  }
  return o as T;
}

/**
 * Глубокая копия состояния: для снимков незавершённого эффекта и «примерок» бота. Журнал и события копируются
 * поверхностно (записи после создания не меняются) — это самая объёмная часть состояния.
 */
export function cloneGame(G: Game): Game {
  // журнал/события/журнал действий копируются поверхностно (только растут); порядок ключей сохраняется —
  // сравнение состояний через JSON (тесты, снимки) не должно зависеть от того, была ли копия
  const g = deepClone({ ...G, log: [], events: [], journal: [], pending: null }) as Game;
  g.log = G.log.slice();
  g.events = G.events.slice();
  if (G.journal) g.journal = G.journal.slice();
  else delete g.journal;
  return g;
}

/** Заменить содержимое состояния G содержимым другого (для отмены действия: G остаётся тем же объектом) */
export function overwriteGame(G: Game, src: Game): void {
  for (const k of Object.keys(G)) delete (G as unknown as Record<string, unknown>)[k];
  Object.assign(G, src);
}

// ---------- создание партии ----------

export interface GameConfig {
  seed?: number;
  players: { name?: string; bot?: boolean; botLevel?: BotLevel; botStyle?: BotStyle; boss?: BossId }[];
  ddkPerPlayer?: number;
  /** режим ребаланса: цены карт из REBALANCE подменяются на время партии (см. src/engine/rebalance.ts) */
  rebalance?: boolean;
  /** версия правил партии (по умолчанию текущая RULES_VERSION); прошлая — для воспроизведения старых журналов */
  rulesVersion?: number;
  /** мутаторы партии (src/engine/mutators.ts); неизвестные и повторы отбрасываются */
  mutators?: readonly MutatorId[];
}

/** Оригинальные цены до первого применения ребаланса (в базе они подменяются на месте) */
const origCost = new Map<CardId, number | null>();
const origText = new Map<CardId, string>();
const origTraitText = new Map<TraitId, string>();

/**
 * Включить/выключить правки ребаланса в общей базе DB. Партии в одном процессе идут по одной
 * (и на сервере — одна партия на VM), поэтому подмена на месте безопасна: каждый createGame выставляет своё,
 * а restoreRuntime восстанавливает по G.rebalance загруженной партии.
 */
/** Базовая (неправленая) цена карты — даже когда в DB сейчас применены цены режима ребаланса */
export function baseCost(id: CardId): number | null {
  return origCost.has(id) ? origCost.get(id) ?? null : DB.cards[id]?.cost ?? null;
}

/** Базовый (неправленый) текст карты — для показа оригинала рядом с правкой в справке режима */
export function baseText(id: CardId): string {
  return origText.has(id) ? origText.get(id) ?? '' : DB.cards[id]?.text ?? '';
}

/** Базовый (неправленый) текст жетона свойства */
export function baseTraitText(id: TraitId): string {
  return origTraitText.has(id) ? origTraitText.get(id) ?? '' : DB.traits[id]?.text ?? '';
}

/**
 * Правка режима подменяет само определение карты (цена И текст) и текст жетона свойства: правленая карта
 * должна ВЫГЛЯДЕТЬ правленой везде, где её показывают, а не нести исходные числа со сноской-опровержением.
 * Оригинал остаётся доступен (baseCost/baseText/baseTraitText) — он нужен справке режима и кнопке «Оригинал».
 */
export function applyRebalance(on: boolean, version: number = RULES_VERSION): void {
  // цены — по таблице версии партии: партия прошлой версии играется по своим ценам до конца
  const prices = version === RULES_VERSION
    ? new Map(Object.entries(REBALANCE).flatMap(([id, p]) => (p.cost != null ? [[id, p.cost] as const] : [])))
    : priceTable(version);
  for (const id of pricePatchedIds()) {
    const def = DB.cards[id];
    if (!def) continue;
    if (!origCost.has(id)) origCost.set(id, def.cost);
    const cost = prices.get(id);
    def.cost = on && cost != null ? cost : origCost.get(id) ?? def.cost;
  }
  for (const [id, patch] of Object.entries(REBALANCE)) {
    const def = DB.cards[id];
    if (!def) continue;
    if (patch.text != null) {
      if (!origText.has(id)) origText.set(id, def.text);
      def.text = on ? patch.text : origText.get(id) ?? def.text;
    }
  }
  for (const [id, def] of Object.entries(DB.traits)) {
    const patch = REBALANCE_TRAIT_NOTES[def.key];
    if (!patch) continue;
    if (!origTraitText.has(id)) origTraitText.set(id, def.text);
    def.text = on ? patch.text : origTraitText.get(id) ?? def.text;
  }
  // остальные подмены на время партии (знания ботов) — у своих модулей
  runModeHooks(on);
}

export function createGame(config: GameConfig): Game {
  if (!DB.raw) throw new Error('База карт не загружена');
  const rulesVersion = config.rulesVersion ?? RULES_VERSION;
  applyRebalance(!!config.rebalance, rulesVersion);
  const seed = (config.seed ?? (Date.now() & 0xffffffff)) >>> 0;
  const rng = makeRng(seed);
  nextIid = 1;
  const mutators = normalizeMutators(config.mutators);
  const G: Game = {
    rng,
    seed,
    ...(config.rebalance ? { rebalance: true } : {}),
    ...(mutators.length ? { mutators } : {}),
    rulesVersion,
    journal: [],
    phase: 'setup',
    players: [],
    mainDeck: [],
    legendDeck: [],
    market: [],
    legendMarket: [],
    marketChips: {},
    chaosPile: [],
    limpPile: [],
    ddkPile: [],
    trash: [],
    trashBespredel: [],
    chipsSupply: RULES.chipsSupply,
    prizeOwner: null,
    turn: null,
    log: [],
    events: [],
    eventSeq: 0,
    prompt: null,
    pending: null,
    setup: { traitOffers: [], familiarOffers: [], freeFamiliars: [] },
    winner: null,
    endTriggered: false,
  };
  config.players.forEach((p, i) => {
    // босс — всегда сложный бот с характером из реестра
    const boss = p.boss && isBossId(p.boss) ? BOSSES[p.boss] : null;
    G.players.push({
      id: i,
      name: p.name || (boss ? bossName(boss.id) : `Игрок ${i + 1}`),
      bot: !!p.bot || !!boss,
      ...(boss ? { botLevel: 'hard' as const, botStyle: boss.style, boss: boss.id } : {}),
      ...(!boss && p.bot && p.botLevel ? { botLevel: p.botLevel } : {}),
      ...(!boss && p.bot && p.botStyle ? { botStyle: p.botStyle } : {}),
      wizard: null,
      traitId: null,
      familiars: [],
      life: startLifeOf({ mutators }),
      chips: 0,
      loser: false,
      deck: [],
      hand: [],
      discard: [],
      inPlay: [],
      permanents: [],
      ddk: [],
      handSize: handSizeOf(G),
    });
  });
  // колоды
  const main: CardInst[] = [];
  const legends: CardInst[] = [];
  for (const c of DB.raw.cards) {
    const count = c.count || 1;
    for (let k = 0; k < count; k++) {
      if (c.type === 'шальная магия') G.chaosPile.push(mk(c.id));
      else if (c.type === 'вялая палочка') G.limpPile.push(mk(c.id));
      else if (c.type === 'мегабеспредел') legends.push(mk(c.id));
      else if (c.legend) legends.push(mk(c.id));
      else if (c.type === 'беспредел') main.push(mk(c.id));
      else if (c.type === 'волшебник' || c.type === 'тварь' || c.type === 'заклинание' || c.type === 'сокровище' || c.type === 'место') main.push(mk(c.id));
    }
  }
  G.mainDeck = shuffle(rng, main);
  G.legendDeck = shuffle(rng, legends);
  G.ddkPile = shuffle(rng, Object.keys(DB.ddk)).slice(0, (config.ddkPerPlayer ?? RULES.ddkPerPlayer) * G.players.length);
  // жетоны свойств и фамильяры — предложения на выбор
  const traitIds = shuffle(rng, Object.keys(DB.traits));
  // боссу предлагается только его фамильяр; остальным — по два из прочих
  const bossFams = new Set(G.players.flatMap((p) => (p.boss ? [BOSSES[p.boss].familiar] : [])));
  const famIds = shuffle(rng, (DB.byType['фамильяр'] ?? []).map((c) => c.id)).filter((id) => !bossFams.has(id));
  const setup = setupOf(G);
  let k = 0;
  G.players.forEach((p, i) => {
    setup.traitOffers.push([traitIds[2 * i], traitIds[2 * i + 1]].filter((x): x is TraitId => !!x));
    if (p.boss) setup.familiarOffers.push([BOSSES[p.boss].familiar]);
    else {
      setup.familiarOffers.push([famIds[k], famIds[k + 1]].filter((x): x is CardId => !!x));
      k += 2;
    }
  });
  setup.freeFamiliars = famIds.slice(k);
  return G;
}

/** Карта с символом чипсины попала на барахолку — на неё ложится чипсина из запаса (правила, стр. 15) */
export function placeMarketChip(G: Game, c: CardInst): void {
  if (card(c).chipsina) addMarketChip(G, c);
}

/** Положить чипсину из общего запаса на карту барахолки (символ чипсины, беспредел «чипсина на барахолку») */
export function addMarketChip(G: Game, c: CardInst): void {
  if (G.chipsSupply <= 0) return;
  G.chipsSupply--;
  const n = (G.marketChips[c.iid] ?? 0) + 1;
  G.marketChips[c.iid] = n;
  log(G, `На «${card(c).name}» ложится чипсина${n > 1 ? ` (на карте ${n})` : ''}`);
  emit(G, { type: 'marketChip', iid: c.iid, cid: c.cid, n });
}

// ---------- хелперы ----------

export function log(G: Game, msg: string): void {
  G.log.push(msg);
  if (G.log.length > 1500) G.log.shift();
}

/** Событие для UI (анимации/звуки/режим наблюдения). seq растёт монотонно. */
export function emit(G: Game, body: EventBody): void {
  const ev = { ...body, seq: ++G.eventSeq, ...(G.turn ? { turn: G.turn.player } : {}) };
  G.events.push(ev);
  if (G.events.length > 400) G.events.shift();
}

/** Враги по часовой от игрока (правила: при нескольких целях эффекты идут по часовой от атакующего) */
export function enemies(G: Game, p: Player): Player[] {
  return clockwiseFrom(G, p).slice(1);
}

/** Слева = следующий по часовой */
export function leftOf(G: Game, p: Player): Player {
  return player(G, (p.id + 1) % G.players.length);
}

export function rightOf(G: Game, p: Player): Player {
  return player(G, (p.id - 1 + G.players.length) % G.players.length);
}

export function clockwiseFrom(G: Game, p: Player): Player[] {
  const n = G.players.length;
  const res: Player[] = [];
  for (let i = 0; i < n; i++) res.push(player(G, (p.id + i) % n));
  return res;
}

export function maxLife(p: Player): number {
  return p.loser ? RULES.loserMaxLife : RULES.maxLife;
}

/**
 * Раскрыта скрытая информация (взята/раскрыта карта колоды). Незавершённое действие после этого отменить
 * нельзя — иначе игрок подглядел бы и передумал. Для «шага назад» (отмены уже завершённого действия)
 * раскрытия делятся на два вида:
 *  - свои карты активного игрока (добор с руки, свой верх колоды) — мягкое: откат разрешаем, но кнопка
 *    честно предупреждает, что добранное уже видно (в хотсите все и так смотрят в один экран);
 *  - чужое и общее (чужая колода, стопка жетонов, барахолка) — жёсткое: откат закрыт.
 * whose — чьи карты раскрыты; без него раскрытие считается общим.
 */
export function touchHidden(G: Game, whose?: PlayerId): void {
  if (G.pending) G.pending.irreversible = true;
  const own = whose != null && G.turn != null && whose === G.turn.player;
  if (own) G.softSeq = (G.softSeq ?? 0) + 1;
  else G.lockSeq = (G.lockSeq ?? 0) + 1;
}

export function drawCards(G: Game, p: Player, n: number): CardInst[] {
  const drawn: CardInst[] = [];
  let reshuffled = false;
  // известные всем карты с верха колоды остаются известными и на руке
  const knownTop = new Set(deckKnown(p).top.map((c) => c.iid));
  for (let i = 0; i < n; i++) {
    if (!p.deck.length) {
      if (!p.discard.length) break;
      p.deck = shuffle(rngOf(G), p.discard);
      p.discard = [];
      reshuffled = true;
    }
    const c = p.deck.pop();
    if (!c) break;
    p.hand.push(c);
    drawn.push(c);
    if (!reshuffled && knownTop.has(c.iid)) markHandKnown(p, [c]);
  }
  if (drawn.length) {
    touchHidden(G, p.id);
    emit(G, { type: 'draw', pid: p.id, n: drawn.length, iids: drawn.map((c) => c.iid), cids: drawn.map((c) => c.cid), reshuffled });
  }
  return drawn;
}

/** Верхняя карта колоды (при пустой колоде сброс замешивается). Карта остаётся в колоде. */
export function revealTop(G: Game, p: Player): CardInst | null {
  if (!p.deck.length) {
    if (!p.discard.length) return null;
    p.deck = shuffle(rngOf(G), p.discard);
    p.discard = [];
  }
  const c = p.deck[p.deck.length - 1] ?? null;
  if (c) touchHidden(G, p.id);
  return c;
}

export function removeFrom(arr: CardInst[], iid: number): CardInst | null {
  const i = arr.findIndex((c) => c.iid === iid);
  if (i >= 0) return arr.splice(i, 1)[0] ?? null;
  return null;
}

export const PLAYER_ZONES: readonly PlayerZone[] = ['deck', 'hand', 'discard', 'inPlay', 'permanents', 'familiars'];
export const GLOBAL_ZONES: readonly GlobalZone[] = ['market', 'legendMarket', 'mainDeck', 'legendDeck', 'chaosPile', 'limpPile', 'trash', 'trashBespredel'];

export interface InstanceLocation {
  card: CardInst;
  owner: Player | null;
  zone: PlayerZone | GlobalZone;
}

export function findInstance(G: Game, iid: number): InstanceLocation | null {
  for (const p of G.players) {
    for (const zone of PLAYER_ZONES) {
      const c = p[zone].find((x) => x.iid === iid);
      if (c) return { card: c, owner: p, zone };
    }
  }
  for (const zone of GLOBAL_ZONES) {
    const c = G[zone].find((x) => x.iid === iid);
    if (c) return { card: c, owner: null, zone };
  }
  return null;
}

/** Наибольший iid среди всех карт партии (для восстановления счётчика после загрузки) */
export function maxIid(G: Game): number {
  let m = 0;
  const scan = (arr: CardInst[]): void => {
    for (const c of arr) m = Math.max(m, c.iid);
  };
  for (const p of G.players) for (const z of PLAYER_ZONES) scan(p[z]);
  for (const z of GLOBAL_ZONES) scan(G[z]);
  return m;
}

/** Получить чипсины из запаса; src — откуда (карта, жетон, беспредел…): попадает в журнал и событие */
export function gainChips(G: Game, p: Player, n: number, src?: string): number {
  const got = Math.min(n, G.chipsSupply);
  G.chipsSupply -= got;
  p.chips += got;
  if (got) {
    log(G, `${p.name} получает ${got} чипсин${src ? ` (${src})` : ''}`);
    emit(G, { type: 'chips', pid: p.id, n: got, ...(src ? { src } : {}) });
  }
  return got;
}

export function spendChips(G: Game, p: Player, n: number, src?: string): number {
  const spent = Math.min(n, p.chips);
  p.chips -= spent;
  G.chipsSupply += spent;
  if (spent) {
    log(G, `${p.name} тратит ${spent} чипсин${src ? ` (${src})` : ''}`);
    emit(G, { type: 'chips', pid: p.id, n: -spent, ...(src ? { src } : {}) });
  }
  return spent;
}

/** Уничтожение карты: шальная магия/вялые палочки — обратно в стопки, беспределы — в свою стопку. owner — чья карта (для ленты) */
export function trashCard(G: Game, c: CardInst, owner?: Player): void {
  const def = card(c);
  emit(G, { type: 'trash', iid: c.iid, cid: c.cid, ...(owner ? { pid: owner.id } : {}) });
  if (def.type === 'шальная магия') G.chaosPile.push(c);
  else if (def.type === 'вялая палочка') G.limpPile.push(c);
  else if (def.type === 'беспредел' || def.type === 'мегабеспредел') G.trashBespredel.push(c);
  else G.trash.push(c);
}

export function isBespredel(c: CardRef | CardId): boolean {
  const t = card(c).type;
  return t === 'беспредел' || t === 'мегабеспредел';
}

/** Получить карту (по умолчанию в сброс). Беспределы уничтожаются. */
export interface GainOpts {
  /** карта передана этим игроком (в журнале — «получает от X») */
  from?: Player;
  /** карта возвращается владельцу (сыграна с его колоды) */
  back?: boolean;
}

const WHERE_LABEL: Record<GainWhere, string> = { discard: '', hand: ' на руку', deckTop: ' на верх колоды', deckBottom: ' под низ колоды', play: '' };

/** Положить карту игроку в указанную зону — с записью в журнал и событием; единственный способ «получить» карту */
export function gainCard(G: Game, p: Player, c: CardInst, where: GainWhere = 'discard', { from, back = false }: GainOpts = {}): boolean {
  const def = card(c);
  if (isBespredel(c)) {
    trashCard(G, c);
    log(G, `${p.name}: полученный беспредел уничтожен`);
    return false;
  }
  if (where === 'hand') {
    p.hand.push(c);
    markHandKnown(p, [c]); // получена у всех на глазах (журнал её называет)
  } else if (where === 'deckTop') {
    p.deck.push(c);
    markKnown(p, c, 'top');
  } else if (where === 'deckBottom') {
    p.deck.unshift(c);
    markKnown(p, c, 'bottom');
  }
  else if (where === 'play') {
    if (def.permanent) p.permanents.push(c);
    else p.inPlay.push(c);
  } else p.discard.push(c);
  if (back) log(G, `«${def.name}» возвращается владельцу — ${p.name}${WHERE_LABEL[where]}`);
  else log(G, `${p.name} получает «${def.name}»${from ? ` от ${from.name}` : ''}${WHERE_LABEL[where]}`);
  emit(G, { type: 'gain', pid: p.id, iid: c.iid, cid: c.cid, where, ...(from ? { from: from.id } : {}), ...(back ? { back } : {}) });
  return true;
}

/** Эти карты на руке видели все (взяты в открытую, показаны); знание живёт, пока карта на руке */
export function markHandKnown(p: Player, cs: readonly CardInst[]): void {
  const inHand = new Set(p.hand.map((c) => c.iid));
  const known = (p.knownHand ?? []).filter((iid) => inHand.has(iid));
  for (const c of cs) if (inHand.has(c.iid) && !known.includes(c.iid)) known.push(c.iid);
  p.knownHand = known;
}

/** Известные всем карты на руке игрока (ушедшие с руки — уже не в счёт) */
export function handKnown(p: Player): CardInst[] {
  const known = new Set(p.knownHand ?? []);
  return known.size ? p.hand.filter((c) => known.has(c.iid)) : [];
}

/** Карта легла на верх/низ колоды у всех на глазах — теперь её положение публично известно */
export function markKnown(p: Player, c: CardInst, where: 'top' | 'bottom'): void {
  pruneKnown(p);
  if (where === 'top') (p.knownTop ??= []).push(c.iid);
  else (p.knownBottom ??= []).unshift(c.iid);
  if ((p.knownTop?.length ?? 0) > 8) p.knownTop = p.knownTop!.slice(-8);
  if ((p.knownBottom?.length ?? 0) > 8) p.knownBottom = p.knownBottom!.slice(0, 8);
}

/** Сверить запомненное с фактическим порядком колоды: добор, перетасовка и уничтожение стирают знание */
function pruneKnown(p: Player): void {
  const d = p.deck;
  if (p.knownTop?.length) {
    let m = 0;
    while (m < p.knownTop.length && d[d.length - 1 - m]?.iid === p.knownTop[p.knownTop.length - 1 - m]) m++;
    p.knownTop = m ? p.knownTop.slice(-m) : [];
  }
  if (p.knownBottom?.length) {
    let m = 0;
    while (m < p.knownBottom.length && d[m]?.iid === p.knownBottom[m]) m++;
    p.knownBottom = m ? p.knownBottom.slice(0, m) : [];
  }
}

/** Публично известные карты колоды: top — от самой верхней вниз, bottom — от самой нижней вверх.
 *  Не меняет состояние (зовётся и из viewFor): сверка с фактическим порядком — локально */
export function deckKnown(p: Player): { top: CardInst[]; bottom: CardInst[] } {
  const d = p.deck;
  const top: CardInst[] = [];
  const kt = p.knownTop ?? [];
  for (let i = 0; i < kt.length; i++) {
    const c = d[d.length - 1 - i];
    if (!c || c.iid !== kt[kt.length - 1 - i]) break;
    top.push(c);
  }
  const bottom: CardInst[] = [];
  const kb = p.knownBottom ?? [];
  for (let i = 0; i < kb.length; i++) {
    const c = d[i];
    if (!c || c.iid !== kb[i] || top.includes(c)) break;
    bottom.push(c);
  }
  return { top, bottom };
}

/** Убрать карту из любой зоны игрока; возвращает зону, где она лежала */
export function removeFromPlayer(_G: Game, p: Player, iid: number): PlayerZone | null {
  for (const z of PLAYER_ZONES) if (removeFrom(p[z], iid)) return z;
  return null;
}

/** Передать карту другому игроку (из любой своей зоны) — журнал «получает от X» и событие gain с from */
export function giveCard(G: Game, from: Player, to: Player, c: CardInst, where: GainWhere = 'discard', opts: Omit<GainOpts, 'from'> = {}): boolean {
  removeFromPlayer(G, from, c.iid);
  return gainCard(G, to, c, where, { ...opts, from });
}

/** Сбросить карты игрока из любых его зон (или уже вынутые из зоны) — журнал и событие discard; label — вместо перечня карт */
export function discardCards(G: Game, p: Player, cards: readonly CardInst[], label?: string): CardInst[] {
  const cs = [...cards];
  if (!cs.length) return cs;
  for (const c of cs) {
    removeFromPlayer(G, p, c.iid);
    p.discard.push(c);
  }
  log(G, `${p.name} сбрасывает ${label ?? cs.map((c) => card(c).name).join(', ')}`);
  emit(G, { type: 'discard', pid: p.id, iids: cs.map((c) => c.iid), cids: cs.map((c) => c.cid) });
  return cs;
}

const MOVE_LABEL = { hand: 'берёт на руку', deckTop: 'кладёт на верх колоды', deck: 'замешивает в колоду' } as const;

/** Перенести свои карты (из любой зоны или уже вынутые) на руку / на верх колоды / в колоду (замешать — забота вызывающего) */
export function moveCards(G: Game, p: Player, cards: readonly CardInst[], to: keyof typeof MOVE_LABEL): CardInst[] {
  const cs = [...cards];
  if (!cs.length) return cs;
  for (const c of cs) {
    removeFromPlayer(G, p, c.iid);
    if (to === 'hand') {
      p.hand.push(c);
      markHandKnown(p, [c]); // журнал называет карты, которые берутся на руку
    } else {
      p.deck.push(c);
      if (to === 'deckTop') markKnown(p, c, 'top');
    }
  }
  log(G, `${p.name} ${MOVE_LABEL[to]}: ${cs.map((c) => card(c).name).join(', ')}`);
  emit(G, { type: 'move', pid: p.id, iids: cs.map((c) => c.iid), cids: cs.map((c) => c.cid), to });
  return cs;
}

export function gainLimp(G: Game, p: Player, where: GainWhere = 'discard'): boolean {
  const c = G.limpPile.pop();
  if (!c) return false;
  return gainCard(G, p, c, where);
}

export function gainChaos(G: Game, p: Player, where: GainWhere = 'discard'): boolean {
  const c = G.chaosPile.pop();
  if (!c) return false;
  return gainCard(G, p, c, where);
}

/** Карты под контролем игрока (в игре): сыгранные в этот ход + постоянки */
export function controlled(_G: Game, p: Player): CardInst[] {
  return [...p.inPlay, ...p.permanents];
}

/** Все карты, которыми владеет игрок (для подсчёта ПО и условий «в колоде»); фамильяры под планшетом не считаются */
export function ownedCards(G: Game, p: Player): CardInst[] {
  // карты, сыгранные с чужой колоды (returnTo), до конца хода лежат у другого игрока, но принадлежат владельцу
  const own = [...p.deck, ...p.hand, ...p.discard, ...p.inPlay.filter((c) => c.returnTo == null), ...p.permanents];
  for (const o of G.players) if (o.id !== p.id) for (const c of o.inPlay) if (c.returnTo === p.id) own.push(c);
  return own;
}

export function countType(cards: readonly CardInst[], type: CardType): number {
  return cards.filter((c) => card(c).type === type).length;
}

export function isLegend(c: CardRef): boolean {
  return card(c).legend;
}

export function isWand(c: CardRef): boolean {
  return hasTag(c, 'wand');
}

export function startTurn(G: Game, pid: PlayerId): void {
  const p = player(G, pid);
  G.turnNo = (G.turnNo ?? 0) + 1;
  G.turn = {
    player: pid,
    power: 0,
    legendDiscount: 0,
    activated: [],
    played: [],
    gainedThisTurn: [],
    spellsGained: 0,
    bought: [],
    extraDraw: 0,
    noDraw: false,
    killsThisTurn: 0,
    needsRefill: true,
    nextUnavoidable: false,
    noDefense: [],
    gainToHand: false,
    brataliti: false,
    tronadoUsed: false,
  };
  log(G, `— Ход: ${p.name} —`);
  emit(G, { type: 'turn', pid });
}

export function nextPlayerId(G: Game): PlayerId {
  return (turnOf(G).player + 1) % G.players.length;
}

export function setIidCounter(n: number): void {
  nextIid = n;
}

export { mk as makeInstance };
